/**
 * STT誤認識修正辞書管理画面
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSttCorrectionDictionary,
  createSttCorrectionDictionaryEntry,
  updateSttCorrectionDictionaryEntry,
  deleteSttCorrectionDictionaryEntry,
} from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

interface SttCorrectionDictionaryEntry {
  id: number;
  misrecognized_text: string;
  correct_text: string;
  description?: string;
  priority: number;
  created: string;
  updated: string;
}

const PAGE_SIZE = 20;

export default function SttCorrectionDictionary() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<SttCorrectionDictionaryEntry | null>(null);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    misrecognized_text: '',
    correct_text: '',
    description: '',
    priority: 0,
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'stt-correction-dictionary'],
    queryFn: async () => {
      const result = await getSttCorrectionDictionary();
      console.log('STT誤認識修正辞書APIレスポンス:', result);
      return result;
    },
  });

  const createMutation = useMutation({
    mutationFn: createSttCorrectionDictionaryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stt-correction-dictionary'] });
      setIsCreateDialogOpen(false);
      setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
      toast({
        title: '成功',
        description: 'STT誤認識修正辞書エントリを作成しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'STT誤認識修正辞書エントリの作成に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateSttCorrectionDictionaryEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stt-correction-dictionary'] });
      setIsEditDialogOpen(false);
      setSelectedEntry(null);
      setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
      toast({
        title: '成功',
        description: 'STT誤認識修正辞書エントリを更新しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'STT誤認識修正辞書エントリの更新に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSttCorrectionDictionaryEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stt-correction-dictionary'] });
      setIsDeleteDialogOpen(false);
      setSelectedEntry(null);
      toast({
        title: '成功',
        description: 'STT誤認識修正辞書エントリを削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'STT誤認識修正辞書エントリの削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleCreate = () => {
    if (!formData.misrecognized_text || !formData.correct_text) {
      toast({
        title: 'エラー',
        description: '誤認識テキストと正しいテキストは必須です',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate({
      misrecognized_text: formData.misrecognized_text,
      correct_text: formData.correct_text,
      description: formData.description || undefined,
      priority: formData.priority || 0,
    });
  };

  const handleEdit = (entry: SttCorrectionDictionaryEntry) => {
    setSelectedEntry(entry);
    setFormData({
      misrecognized_text: entry.misrecognized_text,
      correct_text: entry.correct_text,
      description: entry.description || '',
      priority: entry.priority,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedEntry) return;

    if (!formData.misrecognized_text || !formData.correct_text) {
      toast({
        title: 'エラー',
        description: '誤認識テキストと正しいテキストは必須です',
        variant: 'destructive',
      });
      return;
    }

    updateMutation.mutate({
      id: selectedEntry.id,
      data: {
        misrecognized_text: formData.misrecognized_text,
        correct_text: formData.correct_text,
        description: formData.description || undefined,
        priority: formData.priority || 0,
      },
    });
  };

  const handleDelete = (entry: SttCorrectionDictionaryEntry) => {
    setSelectedEntry(entry);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedEntry) {
      deleteMutation.mutate(selectedEntry.id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">STT誤認識修正辞書管理</h1>
            <p className="text-base text-gray-500 mt-2">音声認識（STT）の誤認識を修正する辞書データの管理</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                <Plus className="w-4 h-4 mr-1.5" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>新規STT誤認識修正辞書エントリ作成</DialogTitle>
                <DialogDescription>新しい誤認識修正エントリを追加します</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="create-misrecognized-text">誤認識テキスト（必須）</Label>
                  <Input
                    id="create-misrecognized-text"
                    value={formData.misrecognized_text}
                    onChange={(e) => setFormData({ ...formData, misrecognized_text: e.target.value })}
                    placeholder="例: 配車"
                  />
                  <p className="text-sm text-gray-500 mt-1">STTで誤って認識されるテキスト</p>
                </div>
                <div>
                  <Label htmlFor="create-correct-text">正しいテキスト（必須）</Label>
                  <Input
                    id="create-correct-text"
                    value={formData.correct_text}
                    onChange={(e) => setFormData({ ...formData, correct_text: e.target.value })}
                    placeholder="例: 歯医者"
                  />
                  <p className="text-sm text-gray-500 mt-1">修正後の正しいテキスト</p>
                </div>
                <div>
                  <Label htmlFor="create-priority">優先度</Label>
                  <Input
                    id="create-priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <p className="text-sm text-gray-500 mt-1">高い値ほど優先的に適用されます（デフォルト: 0）</p>
                </div>
                <div>
                  <Label htmlFor="create-description">説明（任意）</Label>
                  <Textarea
                    id="create-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="例: 「歯医者」が「配車」と誤認識される場合の修正"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsCreateDialogOpen(false);
                  setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
                }}>
                  キャンセル
                </Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '作成'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* STT誤認識修正辞書一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">STT誤認識修正辞書一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました: {error instanceof Error ? error.message : String(error)}</div>
          ) : !data?.entries || data.entries.length === 0 ? (
            <div className="text-center py-8 text-base text-gray-500">
              データがありません。新規作成ボタンからエントリを追加してください。
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                  <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">誤認識テキスト</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">正しいテキスト</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">優先度</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">説明</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((entry: SttCorrectionDictionaryEntry) => (
                  <TableRow key={entry.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{entry.id}</TableCell>
                    <TableCell className="py-4 text-base font-bold text-red-600 text-center">{entry.misrecognized_text}</TableCell>
                    <TableCell className="py-4 text-base font-bold text-green-600 text-center">{entry.correct_text}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">{entry.priority}</TableCell>
                    <TableCell className="py-4 text-base text-gray-700 text-center">{entry.description || '-'}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">
                      {new Date(entry.created).toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => handleEdit(entry)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          aria-label="編集"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label="削除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.entries.length > PAGE_SIZE && (
              <div className="flex justify-between items-center mt-5 pt-4">
                <div className="text-base text-gray-700">
                  全 {data.entries.length} 件中 {((page - 1) * PAGE_SIZE + 1)} - {Math.min(page * PAGE_SIZE, data.entries.length)} 件を表示
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="h-9 rounded-lg border-gray-200"
                  >
                    前へ
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page * PAGE_SIZE >= data.entries.length}
                    className="h-9 rounded-lg border-gray-200"
                  >
                    次へ
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </div>

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>STT誤認識修正辞書エントリ編集</DialogTitle>
              <DialogDescription>STT誤認識修正辞書エントリを編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-misrecognized-text">誤認識テキスト（必須）</Label>
                <Input
                  id="edit-misrecognized-text"
                  value={formData.misrecognized_text}
                  onChange={(e) => setFormData({ ...formData, misrecognized_text: e.target.value })}
                  placeholder="例: 配車"
                />
                <p className="text-sm text-gray-500 mt-1">STTで誤って認識されるテキスト</p>
              </div>
              <div>
                <Label htmlFor="edit-correct-text">正しいテキスト（必須）</Label>
                <Input
                  id="edit-correct-text"
                  value={formData.correct_text}
                  onChange={(e) => setFormData({ ...formData, correct_text: e.target.value })}
                  placeholder="例: 歯医者"
                />
                <p className="text-sm text-gray-500 mt-1">修正後の正しいテキスト</p>
              </div>
              <div>
                <Label htmlFor="edit-priority">優先度</Label>
                <Input
                  id="edit-priority"
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
                <p className="text-sm text-gray-500 mt-1">高い値ほど優先的に適用されます</p>
              </div>
              <div>
                <Label htmlFor="edit-description">説明（任意）</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="例: 「歯医者」が「配車」と誤認識される場合の修正"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedEntry(null);
                setFormData({ misrecognized_text: '', correct_text: '', description: '', priority: 0 });
              }}>
                キャンセル
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>STT誤認識修正辞書エントリを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。STT誤認識修正辞書エントリID「{selectedEntry?.id}」（{selectedEntry?.misrecognized_text} → {selectedEntry?.correct_text}）を削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
